import { stringify, waitAsync } from '@/Functions';
import { localStorageDB } from '../LocalStorage';
import { Competency, ProfileType } from './types';
import { SpecificGoals, UserPurpose } from 'ai-api/types/purpose';
import { SQLite, SQLiteDatabase } from '../WebDatabase';

/**
 * Interface for SQLite database operations for user profiles
 */
interface IUserDatabase {
    init(): Promise<void>;
    saveProfiles(profiles: ProfileType[]): Promise<void>;
    getAllProfiles(): Promise<ProfileType[]>;
    deleteProfile(userId: string): Promise<void>;
    saveCompetencies(competencies: Competency[]): Promise<void>;
    getUserCompetencies(): Promise<Competency[]>;
    savePurpose(purpose: UserPurpose): Promise<void>;
    getPurpose(): Promise<UserPurpose | null>;
    deletePurpose(): Promise<void>;
    release(): void;
}

/**
 * SQLite database service for user profile operations
 */
class UserDatabase implements IUserDatabase {
    private db: SQLiteDatabase | null = null;
    private semaphore: boolean = false;

    private acquireSemaphore = async () => {
        let count = 0;
        while (this.semaphore && count < 100) { await waitAsync(50); count++; }
        if (this.semaphore) { throw new Error('Failed to acquire purchase semaphore: timeout'); }
        this.semaphore = true;
    }

    private releaseSemaphore = () => this.semaphore = false;
    
    async dropTables(): Promise<void> {
        await this.acquireSemaphore();
        await this.dropTablesUnsafe();
        this.releaseSemaphore();
    }

    private async dropTablesUnsafe(): Promise<void> {
        let now = Math.floor(Date.now() / 1000);
        if (!this.db) this.db = await SQLite.openDatabaseAsync('user.db');
        // assume that the database is already initialized
        try {
            await this.db!.execAsync(`
                DROP TABLE IF EXISTS profiles
            `);
            await this.db!.execAsync(`
                DROP TABLE IF EXISTS competency
            `);
            await this.db!.execAsync(`
                DROP TABLE IF EXISTS purpose
            `);
            this.db?.closeSync();
            this.db = null;
        } catch (error) {
            console.error('Failed to drop user tables:', error);
        }
        console.log(`User tables dropped successfully. time elapsed:${Date.now() / 1000 - now}`);
    }
    
    /**
     * Initialize database and create tables if they don't exist
     */
    async init(): Promise<void> {
        if (this.db) {return;}
        await this.acquireSemaphore();
        try {
            
            await localStorageDB.init();
            const db_version = localStorageDB.getItem('db_version:user');

            // Updated to 1.4 to include phonenumber field
            const LATEST_VERSION = '1.4';
            if (db_version && db_version !== LATEST_VERSION) {
                await this.dropTablesUnsafe();
            }
            await localStorageDB.setItem('db_version:user', LATEST_VERSION);
            this.db = await SQLite.openDatabaseAsync('user.db');
            
            // Create profiles table
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS profiles (
                    userid TEXT PRIMARY KEY UNIQUE,
                    memberState TEXT NOT NULL,
                    addressid TEXT NOT NULL UNIQUE,
                    name TEXT,
                    email TEXT UNIQUE,
                    image TEXT,
                    phonenumber TEXT,
                    birthday TEXT,
                    gender TEXT,
                    updated_at INTEGER NOT NULL
                )
            `);
            
            // Create index for email lookups
            await this.db.execAsync(`
                CREATE INDEX IF NOT EXISTS idx_profiles_email ON profiles(email)
            `);
            
            // Create competency table - only for the current user
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS competency (
                    evaluationid TEXT PRIMARY KEY UNIQUE,
                    sessionid TEXT,
                    attemptid TEXT,
                    evaluate_at INTEGER NOT NULL,
                    scores TEXT NOT NULL,
                    created_at INTEGER NOT NULL,
                    updated_at INTEGER NOT NULL
                )
            `);
            
            // Create purpose table - for storing user's learning goals and purposes
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS purpose (
                    \`primary\` TEXT NOT NULL,
                    secondary TEXT,
                    specific_goals TEXT,
                    created_at INTEGER NOT NULL,
                    updated_at INTEGER NOT NULL
                )
            `);
            
            this.releaseSemaphore();
        } catch (error) {
            this.releaseSemaphore();
            console.error('Failed to initialize user database:', error);
            throw error;
        }
    }
    
    /**
     * Save user profiles to database
     */
    async saveProfiles(profiles: ProfileType[]): Promise<void> {
        if (!this.db) await this.init();
        if (profiles.length === 0) return;
        
        try {
            
            // Save profile data
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO profiles 
            (
                userid, memberState, addressid, 
                name, email, image, phonenumber, birthday, gender,
                updated_at
            )
            VALUES
            ${profiles.map(profile => `(
                ${stringify(profile.userid || "")},
                ${stringify(profile.memberState)},
                ${stringify(profile.addressid)},
                ${stringify(profile.name)},
                ${stringify(profile.email)},
                ${stringify(profile.image || '')},
                ${stringify(profile.phonenumber || '')},
                ${stringify(profile.birthday)},
                ${stringify(profile.gender)},
                ${Math.floor(Date.now() / 1000)}
            )`).join(',\n')}`)
            
        } catch (error) {
            console.error('Failed to save profiles:', error);
            throw error;
        }
    }
    
    /**
     * Get all profiles
     */
    async getAllProfiles(): Promise<ProfileType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<ProfileType>(`
                SELECT * FROM profiles ORDER BY updated_at DESC
            `);
            
            return results || [];
        } catch (error) {
            console.error('Failed to get all profiles:', error);
            return [];
        }
    }
    
    /**
     * Delete a profile
     */
    async deleteProfile(userId: string): Promise<void> {
        if (!this.db) await this.init();
        if (!userId) return;
        
        try {
            await this.db!.execAsync(`
                DELETE FROM profiles WHERE userid = ${stringify(userId)}
            `);
        } catch (error) {
            console.error(`Failed to delete profile ${userId}:`, error);
            throw error;
        }
    }
    
    /**
     * Save competency evaluations to database
     */
    async saveCompetencies(competencies: Competency[]): Promise<void> {
        if (!this.db) await this.init();
        if (competencies.length === 0) return;
        
        try {
            // Save competency data
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO competency 
            (
                evaluationid, sessionid, attemptid,
                evaluate_at, scores,
                created_at, updated_at
            )
            VALUES
            ${competencies.map(comp => `(
                ${stringify(comp.evaluationid)},
                ${stringify(comp.sessionid || null)},
                ${stringify(comp.attemptid || null)},
                ${stringify(comp.evaluate_at)},
                ${stringify(JSON.stringify(comp.scores))},
                ${stringify(comp.created_at)},
                ${Math.floor(Date.now() / 1000)}
            )`).join(',\n')}`)
            
        } catch (error) {
            console.error('Failed to save competency evaluations:', error);
            throw error;
        }
    }
    
    /**
     * Get all user competency evaluations
     */
    async getUserCompetencies(): Promise<Competency[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<{
                evaluationid: string;
                sessionid: string | null;
                attemptid: string | null;
                evaluate_at: number;
                scores: string;
                created_at: number;
                updated_at: number;
            }>(`
                SELECT
                    evaluationid, sessionid, attemptid,
                    evaluate_at, scores,
                    created_at, updated_at
                FROM competency
                ORDER BY evaluate_at DESC
            `);
            
            return results.map(row => ({
                evaluationid: row.evaluationid,
                sessionid: row.sessionid || undefined,
                attemptid: row.attemptid || undefined,
                evaluate_at: row.evaluate_at, 
                scores: JSON.parse(row.scores),
                created_at: row.created_at,
                updated_at: row.updated_at
            }));
        } catch (error) {
            console.error('Failed to get competency evaluations:', error);
            return [];
        }
    }
    
    /**
     * Save user purpose to database
     */
    async savePurpose(purpose: UserPurpose): Promise<void> {
        if (!this.db) await this.init();
        
        const now = Math.floor(Date.now() / 1000);
        
        try {
            await this.db!.execAsync(`
                INSERT OR REPLACE INTO purpose 
                (
                    \`primary\`, secondary,
                    specific_goals, created_at, updated_at
                )
                VALUES (
                    ${stringify(purpose.primary)},
                    ${stringify(JSON.stringify(purpose.secondary || []))},
                    ${stringify(JSON.stringify(purpose.specific_goals || {}))},
                    ${purpose.created_at || now},
                    ${purpose.updated_at || now}
                )
            `);
        } catch (error) {
            console.error('Failed to save user purpose:', error);
            throw error;
        }
    }
    
    /**
     * Get user purpose from database
     */
    async getPurpose(): Promise<UserPurpose | null> {
        if (!this.db) await this.init();
        
        try {
            const result = await this.db!.getFirstAsync<{
                primary: string;
                secondary: string;
                specific_goals: string;
                created_at: number;
                updated_at: number;
            }>(`
                SELECT \`primary\`, secondary,
                       specific_goals, created_at, updated_at
                FROM purpose
            `);
            
            if (!result) return null;
            
            // Parse JSON strings back to objects
            return {
                primary: result.primary as 'prep' | 'enjoyment' | 'conversation',
                secondary: JSON.parse(result.secondary || '[]'),
                specific_goals: JSON.parse(result.specific_goals || '{}') as SpecificGoals,
                created_at: result.created_at,
                updated_at: result.updated_at
            };
        } catch (error) {
            console.error('Failed to get user purpose:', error);
            return null;
        }
    }
    
    /**
     * Delete user purpose from database
     */
    async deletePurpose(): Promise<void> {
        if (!this.db) await this.init();
        
        try {
            await this.db!.execAsync(`
                DELETE FROM purpose
            `);
        } catch (error) {
            console.error('Failed to delete user purpose:', error);
            throw error;
        }
    }
    
    release(): void {
        if (this.db) {
            this.db.closeSync();
            this.db = null;
        }
    }
}

// Export singleton instance
export const userDatabase = new UserDatabase();